import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  mlaAuthors,
  mlaInTextAuthors,
  type PersonName,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaPageRange,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaInText,
  chicagoPageRange,
  doiUrl,
  parsePeople,
  publisherName,
  type SpecialValues,
  splitPages,
} from "@/lib/tool-kit/special-sources/common";

/*
 * Tests, scales, questionnaires and inventories in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 §10.11 (tests, scales and inventories), as the McGill, Buena Vista and
 *   Cambridge library guides reproduce it: "To cite a test, scale, or inventory,
 *   provide a citation for its supporting literature (e.g., its manual, which may be
 *   an authored or edited book, or the journal article in which it was published)";
 *   test database records such as PsycTESTS "provide unique descriptive and
 *   administrative information about tests; cite the database record if you use this
 *   unique information" — "Alonso-Tapia, J., Nieto, C., Merino-Tejedor, E., Huertas,
 *   J. A., & Ruiz, M. (2018). Situated Goals Questionnaire for University Students
 *   (SGQ-U, CMS-U) [Database record]. PsycTESTS. https://doi.org/10.1037/t66267-000";
 *   a manual is cited as a book; the test itself only when there is no manual or
 *   other supporting literature. APA's "Toolbox references" page applies the same
 *   rule to a tool published in an article: cite the article and name the tool in
 *   your sentence. Unpublished works take a bracketed description (§9.21; "you may
 *   alter the wording") and the department and university (§10.8).
 * - APA 7 §6.25 ("Definition of abbreviations"): a citation that accompanies an
 *   abbreviation follows it after a semicolon — "Clinician-Administered PTSD Scale
 *   for DSM-5 (CAPS-5; Weathers et al., 2018)", or "(… [CAPS-5]; Weathers et al.,
 *   2018)" inside parentheses. §6.18: test names are capitalized in the text.
 * - MLA 9 and Chicago 18 have no rules of their own for tests: each source is cited
 *   by its type — the manual as a book, the article as a journal article, the
 *   database record as an item in its database. MLA Style Center, "How do I cite a
 *   survey or quiz that I took?" (June 15, 2022), puts a survey's or quiz's title in
 *   quotation marks with the platform as its container ("Your Feedback on the 2021
 *   Convention." SurveyMonkey, …), so the record and the unpublished questionnaire
 *   take quotation marks here; Chicago likewise quotes the titles of unpublished
 *   works and of items within a database or website.
 * - Examples: the PsycTESTS record is APA's own example (Crossref:
 *   10.1037/t66267-000); the PHQ-9 article is Kroenke, Spitzer & Williams, Journal of
 *   General Internal Medicine 16(9), 606–613 (Crossref:
 *   10.1046/j.1525-1497.2001.016009606.x).
 */

export const QUESTIONNAIRE_FIELDS = {
  authors: "authors",
  year: "year",
  title: "title",
  database: "database",
  publisher: "publisher",
  journal: "journal",
  volume: "volume",
  issue: "issue",
  pages: "pages",
  institution: "institution",
  description: "description",
  doi: "doi",
  testName: "testName",
  testAbbreviation: "testAbbreviation",
} as const;

const F = QUESTIONNAIRE_FIELDS;

export type QuestionnaireSource =
  | "record"
  | "manual"
  | "article"
  | "unpublished";

export interface QuestionnaireLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  namingTest: string;
  worksCited: string;
  inText: string;
  footnote: string;
  bibliography: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Measure {
  source: QuestionnaireSource;
  authors: PersonName[];
  /** The year, or "n.d." — APA and Chicago. */
  year: string;
  /** The year as typed — MLA leaves a missing date out. */
  printedYear: string;
  title: string;
  database: string;
  publisher: string;
  journal: string;
  volume: string;
  issue: string;
  pages: [string, string];
  institution: string;
  description: string;
  link: string;
  testName: string;
  testAbbreviation: string;
}

function readMeasure(
  values: SpecialValues,
  source: QuestionnaireSource
): Measure {
  const typed = get(values, F.doi);
  return {
    source,
    authors: parsePeople(get(values, F.authors)),
    year: get(values, F.year) || "n.d.",
    printedYear: get(values, F.year),
    title: get(values, F.title),
    database: get(values, F.database) || "PsycTESTS",
    publisher: get(values, F.publisher),
    journal: get(values, F.journal),
    volume: get(values, F.volume),
    issue: get(values, F.issue),
    pages: splitPages(get(values, F.pages)),
    institution: get(values, F.institution),
    description:
      get(values, F.description) ||
      (source === "record" ? "Database record" : "Unpublished questionnaire"),
    link: doiUrl(typed) ?? typed,
    testName: get(values, F.testName),
    testAbbreviation: get(values, F.testAbbreviation),
  };
}

export function questionnaireIsReady(values: SpecialValues): boolean {
  return get(values, F.title) !== "";
}

/* ------------------------------------------------------------------- APA */

/** "(Kroenke et al., 2001)" — or the title, when no author is given. */
function apaWho(measure: Measure, narrative: boolean): CitationParts {
  if (measure.authors.length > 0) {
    return [plain(apaInText(measure.authors, measure.year, narrative))];
  }
  const title = titleCase(measure.title.split(":")[0].trim());
  const shown =
    measure.source === "article" ? plain(`“${title}”`) : italic(title);
  return narrative
    ? [shown, plain(` (${measure.year})`)]
    : [plain("("), shown, plain(`, ${measure.year})`)];
}

/** The title and what follows it, up to the source element. */
function apaTitlePart(measure: Measure): CitationParts {
  const title = capitalizeSubtitle(measure.title);
  if (measure.source === "article") {
    return [plain(`${title}${closer(title)}`)];
  }
  if (measure.source === "manual") {
    return [italic(title), plain(closer(title))];
  }
  return [italic(title), plain(` [${measure.description}].`)];
}

/** The source element: journal, database, publisher or institution, then the link. */
function apaSourcePart(measure: Measure): CitationParts {
  const link = measure.link ? ` ${measure.link}` : "";
  if (measure.source === "article") {
    const pages = measure.pages[1]
      ? `${measure.pages[0]}–${measure.pages[1]}`
      : measure.pages[0];
    const issue = measure.issue ? `(${measure.issue})` : "";
    return [
      italic(measure.journal),
      plain(measure.volume ? ", " : ""),
      italic(measure.volume),
      plain(`${issue}${pages ? `, ${pages}` : ""}.${link}`),
    ];
  }
  let source = "";
  if (measure.source === "record") {
    source = measure.database;
  } else if (measure.source === "unpublished") {
    source = measure.institution;
  } else {
    source = publisherName(measure.publisher, "apa");
  }
  return [plain(`${source ? `${source}${closer(source)}` : ""}${link}`)];
}

/*
 * APA: Author. (Year). Title [Description]. Source. DOI — with no author, the title
 * moves to the front and the date follows it (§9.12).
 */
function apaReference(measure: Measure): CitationParts {
  const date = plain(` (${measure.year}). `);
  const authors = apaAuthors(measure.authors);
  if (authors === "") {
    return [...apaTitlePart(measure), date, ...apaSourcePart(measure)];
  }
  return [
    plain(`${authors}${closer(authors)}`),
    date,
    ...apaTitlePart(measure),
    plain(" "),
    ...apaSourcePart(measure),
  ];
}

/** "Patient Health Questionnaire-9 (PHQ-9; Kroenke et al., 2001)" (APA §6.25). */
function namingTest(measure: Measure): string {
  if (measure.testName === "" || measure.authors.length === 0) {
    return "";
  }
  const who = `${apaInTextAuthors(measure.authors)}, ${measure.year}`;
  return measure.testAbbreviation
    ? `${measure.testName} (${measure.testAbbreviation}; ${who})`
    : `${measure.testName} (${who})`;
}

function apaOutput(
  measure: Measure,
  labels: QuestionnaireLabels
): CitationOutput {
  const entries = [
    { label: labels.referenceList, parts: tidyParts(apaReference(measure)) },
    { label: labels.parenthetical, parts: apaWho(measure, false) },
    { label: labels.narrative, parts: apaWho(measure, true) },
  ];
  const naming = namingTest(measure);
  if (naming) {
    entries.push({ label: labels.namingTest, parts: [plain(naming)] });
  }
  return { key: "apa", label: labels.apa, entries, note: labels.apaNote };
}

/* ------------------------------------------------------------------- MLA */

function mlaReference(measure: Measure): CitationParts {
  const authors = mlaAuthors(measure.authors);
  const who = authors ? `${authors}${closer(authors)} ` : "";
  const title = titleCase(measure.title);
  const year = measure.printedYear;
  if (measure.source === "article") {
    const location = [
      measure.volume ? `vol. ${measure.volume}` : "",
      measure.issue ? `no. ${measure.issue}` : "",
      year,
      measure.pages[0]
        ? `${measure.pages[1] ? "pp." : "p."} ${mlaPageRange(...measure.pages)}`
        : "",
      measure.link,
    ]
      .filter(Boolean)
      .join(", ");
    return [
      plain(`${who}“${title}${closer(title)}” `),
      italic(measure.journal),
      plain(`, ${location}.`),
    ];
  }
  if (measure.source === "record") {
    // A record is part of the database that holds it: title in quotation marks.
    const location = [year, measure.link].filter(Boolean).join(", ");
    return [
      plain(`${who}“${title}${closer(title)}” `),
      italic(measure.database),
      plain(location ? `, ${location}.` : "."),
    ];
  }
  if (measure.source === "unpublished") {
    const description = [measure.description, measure.institution]
      .filter(Boolean)
      .join(", ");
    return [
      plain(
        `${who}“${title}${closer(title)}”${year ? ` ${year}.` : ""} ${description}.`
      ),
    ];
  }
  const publisher = publisherName(measure.publisher, "mla");
  const facts = [publisher, year, measure.link].filter(Boolean).join(", ");
  return [
    plain(who),
    italic(title),
    plain(`${closer(title)}${facts ? ` ${facts}.` : ""}`),
  ];
}

function mlaOutput(
  measure: Measure,
  labels: QuestionnaireLabels
): CitationOutput {
  let inText: CitationParts;
  if (measure.authors.length > 0) {
    inText = [plain(`(${mlaInTextAuthors(measure.authors)})`)];
  } else {
    inText = [plain("("), italic(titleCase(measure.title)), plain(")")];
  }
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(mlaReference(measure)) },
      { label: labels.inText, parts: inText },
    ],
    note: labels.mlaNote,
  };
}

/* --------------------------------------------------------------- Chicago */

function chicagoArticle(measure: Measure, note: boolean): CitationParts {
  const title = titleCase(measure.title);
  const pages = chicagoPageRange(...measure.pages);
  const volume = [measure.volume, measure.issue ? `no. ${measure.issue}` : ""]
    .filter(Boolean)
    .join(", ");
  const tail = `${volume ? ` ${volume}` : ""} (${measure.year})${pages ? `: ${pages}` : ""}`;
  const separator = note ? "," : ".";
  const link = measure.link ? `${separator} ${measure.link}` : "";
  return [
    plain(`“${title}${note ? "," : closer(title)}” `),
    italic(measure.journal),
    plain(`${tail}${link}.`),
  ];
}

function chicagoNote(measure: Measure): CitationParts {
  const names = chicagoNoteAuthors(measure.authors);
  const who = names ? `${names}, ` : "";
  const title = titleCase(measure.title);
  if (measure.source === "article") {
    return [plain(who), ...chicagoArticle(measure, true)];
  }
  const link = measure.link ? `, ${measure.link}` : "";
  if (measure.source === "unpublished") {
    const where = [
      measure.description.toLowerCase(),
      measure.institution,
      measure.year,
    ]
      .filter(Boolean)
      .join(", ");
    return [plain(`${who}“${title}” (${where})${link}.`)];
  }
  if (measure.source === "record") {
    return [
      plain(`${who}“${title},” ${measure.database}, ${measure.year}${link}.`),
    ];
  }
  const publisher = publisherName(measure.publisher, "chicago");
  const facts = [publisher, measure.year].filter(Boolean).join(", ");
  return [plain(who), italic(title), plain(` (${facts})${link}.`)];
}

function chicagoBibliography(measure: Measure): CitationParts {
  const names = chicagoBibliographyAuthors(measure.authors);
  const who = names ? `${names}${closer(names)} ` : "";
  const title = titleCase(measure.title);
  if (measure.source === "article") {
    return [plain(who), ...chicagoArticle(measure, false)];
  }
  const link = measure.link ? ` ${measure.link}.` : "";
  if (measure.source === "unpublished") {
    const where = [measure.description, measure.institution, measure.year]
      .filter(Boolean)
      .join(", ");
    return [plain(`${who}“${title}${closer(title)}” ${where}.${link}`)];
  }
  if (measure.source === "record") {
    return [
      plain(
        `${who}“${title}${closer(title)}” ${measure.database}, ${measure.year}.${link}`
      ),
    ];
  }
  const publisher = publisherName(measure.publisher, "chicago");
  const facts = [publisher, measure.year].filter(Boolean).join(", ");
  return [
    plain(who),
    italic(title),
    plain(`${closer(title)} ${facts}${closer(facts)}${link}`),
  ];
}

export function formatQuestionnaire(
  values: SpecialValues,
  source: QuestionnaireSource,
  labels: QuestionnaireLabels
): CitationOutput[] {
  const measure = readMeasure(values, source);
  return [
    apaOutput(measure, labels),
    mlaOutput(measure, labels),
    {
      key: "chicago",
      label: labels.chicago,
      entries: [
        { label: labels.footnote, parts: tidyParts(chicagoNote(measure)) },
        {
          label: labels.bibliography,
          parts: tidyParts(chicagoBibliography(measure)),
        },
      ],
      note: labels.chicagoNote,
    },
  ];
}
